import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from './Toast';
import { ApiService } from '../utils/api';
import { normalizeFieldName } from '../utils/fieldUtils';
import { Upload, Download, X, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const BulkUploadModal = ({ eventId, formSchema = [], isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const { showSuccess, showError } = useToast();

  const handleFileSelect = (selectedFile) => {
    if (selectedFile && (selectedFile.type === 'text/csv' || 
        selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        selectedFile.type === 'application/vnd.ms-excel')) {
      setFile(selectedFile);
      setUploadResult(null);
    } else {
      showError('Please select a valid CSV or Excel file (.csv, .xlsx, .xls)');
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      showError('Please select a file to upload');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const result = await ApiService.bulkUploadRegistrations(eventId, formData);
      setUploadResult(result);
      
      if (result.successful > 0) {
        showSuccess(`Successfully processed ${result.successful} registrations`);
        if (onSuccess) onSuccess(result);
      }
      
      if (result.failed > 0) {
        showError(`${result.failed} registrations failed to process`);
      }
    } catch (error) {
      console.error('Bulk upload error:', error);
      showError('Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const downloadSampleTemplate = () => {
    // Generate CSV template based on form schema
    const headers = formSchema.map(field => normalizeFieldName(field.label || field.name));
    
    // Create sample data based on field types
    const sampleRows = [
      headers.map(header => {
        // Generate sample data based on field type
        switch (header.toLowerCase()) {
          case 'Name':
            return 'John Doe';
          case 'email':
            return 'john@example.com';
          case 'phone':
            return '+1-555-0123';
          case 'company':
            return 'Acme Corp';
          case 'category':
            return 'Speaker';
          case 'booth':
            return 'Booth 123';
          case 'title':
            return 'Software Engineer';
          case 'department':
            return 'Engineering';
          case 'id':
            return 'EMP001';
          default:
            return 'Sample Data';
        }
      }),
      headers.map(header => {
        switch (header.toLowerCase()) {
          case 'Name':
            return 'Jane Smith';
          case 'email':
            return 'jane@example.com';
          case 'phone':
            return '+1-555-0124';
          case 'company':
            return 'Tech Inc';
          case 'category':
            return 'Delegate';
          case 'booth':
            return 'Booth 124';
          case 'title':
            return 'Product Manager';
          case 'department':
            return 'Product';
          case 'id':
            return 'EMP002';
          default:
            return 'Sample Data';
        }
      })
    ];

    const csvContent = [headers, ...sampleRows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_registrations.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const resetModal = () => {
    setFile(null);
    setUploadResult(null);
    setIsUploading(false);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Bulk Upload Registrations</h3>
            <Button variant="outline" onClick={handleClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="space-y-6">
            {/* Instructions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Instructions</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-gray-600 space-y-2">
                <p>• Upload a CSV or Excel file with registration data</p>
                <p>• Required columns: Name, Email</p>
                <p>• Optional columns: Phone, Company, Category, Ticket Type, T-Shirt Size</p>
                <p>• Maximum file size: 10MB</p>
                <p>• Supported formats: .csv, .xlsx, .xls</p>
              </CardContent>
            </Card>

            {/* Sample Template Download */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h4 className="font-medium">Need a template?</h4>
                <p className="text-sm text-gray-600">Download our sample CSV template</p>
              </div>
              <Button variant="outline" onClick={downloadSampleTemplate}>
                <Download className="h-4 w-4 mr-2" />
                Download Template
              </Button>
            </div>

            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select File
              </label>
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={(e) => handleFileSelect(e.target.files[0])}
                  className="hidden"
                />
                
                {file ? (
                  <div className="space-y-2">
                    <CheckCircle className="h-8 w-8 text-green-500 mx-auto" />
                    <p className="text-sm font-medium text-gray-900">{file.name}</p>
                    <p className="text-xs text-gray-500">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setFile(null)}
                    >
                      Remove File
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="h-8 w-8 text-gray-400 mx-auto" />
                    <div>
                      <p className="text-sm text-gray-600">
                        Drag and drop your file here, or{' '}
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="text-blue-600 hover:text-blue-500 font-medium"
                        >
                          browse
                        </button>
                      </p>
                      <p className="text-xs text-gray-500">
                        CSV, XLSX, XLS up to 10MB
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Upload Button */}
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={!file || isUploading}
                className="flex items-center space-x-2"
              >
                {isUploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    <span>Upload</span>
                  </>
                )}
              </Button>
            </div>

            {/* Upload Results */}
            {uploadResult && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Upload Results</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Total Processed:</span>
                      <span className="font-medium">{uploadResult.processed}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-green-600">Successful:</span>
                      <span className="font-medium text-green-600">{uploadResult.successful}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-red-600">Failed:</span>
                      <span className="font-medium text-red-600">{uploadResult.failed}</span>
                    </div>
                    
                    {uploadResult.errors && uploadResult.errors.length > 0 && (
                      <div className="mt-4">
                        <h5 className="text-sm font-medium text-gray-700 mb-2">Errors:</h5>
                        <div className="max-h-32 overflow-y-auto space-y-1">
                          {uploadResult.errors.map((error, index) => (
                            <div key={index} className="flex items-start space-x-2 text-xs">
                              <XCircle className="h-3 w-3 text-red-500 mt-0.5 flex-shrink-0" />
                              <span className="text-red-600">
                                Row {error.row}: {error.error}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
